import _extends from "@babel/runtime/helpers/extends";

/** @jsx jsx */
import { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { jsx } from '@emotion/core';
import { useCallbackWithAnalytics } from '@atlaskit/analytics-next';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import { ExitingPersistence, ShrinkOut } from '@atlaskit/motion';
import GlobalTheme from '@atlaskit/theme/components';
// eslint-disable-next-line import/order
import { getThemeColors } from '../../../theme';
const packageName = "@atlaskit/tag";
const packageVersion = "11.2.2";
import BaseTag from '../shared/base';
import Before from '../shared/before';
import Content from '../shared/content';
import { chromeLinkStyles, chromeStyles, roundedBorderStyles } from '../shared/styles';
import { mergeRefs } from '../shared/utils';
import { removeButtonStyles } from './styles';

const defaultBeforeRemoveAction = () => true;

const noop = () => {};

var TagStatus;

(function (TagStatus) {
  TagStatus["Showing"] = "showing";
  TagStatus["Removing"] = "removing";
  TagStatus["Removed"] = "removed";
})(TagStatus || (TagStatus = {}));

const InnerRemovableTag = /*#__PURE__*/forwardRef((props, ref) => {
  const [status, setStatus] = useState(TagStatus.Showing);
  const [isHoverCloseButton, setIsHoverCloseButton] = useState(false);
  const {
    appearance = 'default',
    elemBefore = null,
    isRemovable = true,
    text = '',
    color = 'standard',
    mode = 'light',
    href,
    removeButtonLabel,
    testId,
    onBeforeRemoveAction = defaultBeforeRemoveAction,
    onAfterRemoveAction = noop
  } = props;
  const isRounded = appearance === 'rounded';
  const isLink = Boolean(href);
  const onAfterRemoveActionWithAnalytics = useCallbackWithAnalytics(onAfterRemoveAction, {
    action: 'removed',
    actionSubject: 'tag',
    attributes: {
      componentName: 'tag',
      packageName,
      packageVersion
    }
  }, 'atlaskit');
  const handleRemoveComplete = useCallback(() => {
    onAfterRemoveActionWithAnalytics(text);
    setStatus(TagStatus.Removed);
  }, [onAfterRemoveActionWithAnalytics, text]);
  const handleRemoveRequest = useCallback(() => {
    if (onBeforeRemoveAction && onBeforeRemoveAction()) {
      handleRemoveComplete();
    }
  }, [handleRemoveComplete, onBeforeRemoveAction]);
  const onKeyPress = useCallback(e => {
    const spacebarOrEnter = e.key === ' ' || e.key === 'Enter';

    if (spacebarOrEnter) {
      e.stopPropagation();
      handleRemoveRequest();
    }
  }, [handleRemoveRequest]);
  const removingTag = useCallback(() => setStatus(TagStatus.Removing), []);
  const showingTag = useCallback(() => setStatus(TagStatus.Showing), []);
  const handleHoveringRemoveButton = useCallback(isHover => setIsHoverCloseButton(isHover), []);
  const {
    chromeColors,
    chromeLinkColors,
    buttonColors,
    linkHoverColor
  } = useMemo(() => getThemeColors(color, mode, true), [color, mode]);
  const chromeContainerForLinkStyles = [chromeLinkStyles(chromeLinkColors), isRounded ? roundedBorderStyles : undefined];
  const chromeContainerStyles = [chromeStyles({ ...chromeColors
  }), isRounded ? roundedBorderStyles : undefined];
  const removeButton = isRemovable ? jsx("button", {
    css: [removeButtonStyles({ ...buttonColors
    }), isRounded ? roundedBorderStyles : undefined],
    tabIndex: 0,
    "aria-label": `${removeButtonLabel} ${text}`,
    onClick: handleRemoveRequest,
    onFocus: removingTag,
    onBlur: showingTag,
    onKeyPress: onKeyPress,
    onMouseOver: () => isLink && handleHoveringRemoveButton(true),
    onMouseOut: () => isLink && handleHoveringRemoveButton(false),
    type: "button",
    "data-testid": `close-button-${testId}`
  }, jsx(EditorCloseIcon, {
    label: "close tag",
    size: "small"
  })) : undefined;
  const tagCss = [...chromeContainerStyles, isLink ? chromeContainerForLinkStyles : undefined];
  const content = jsx(Content, _extends({}, props, {
    isRemovable: isRemovable,
    isLink: isLink,
    isRounded: isRounded,
    linkHoverColor: linkHoverColor
  }));
  return jsx(ExitingPersistence, null, !(status === TagStatus.Removed) && jsx(ShrinkOut, null, motion => {
    return jsx(BaseTag, {
      ref: mergeRefs(motion.ref, ref),
      testId: testId,
      tagCss: tagCss,
      "data-removable": true,
      "data-removing": status === TagStatus.Removing,
      "data-ishoverclosebutton": isHoverCloseButton,
      before: jsx(Before, {
        isRounded: isRounded,
        elemBefore: elemBefore,
        styles: chromeColors
      }),
      contentElement: content,
      after: removeButton
    });
  }));
});
const RemovableTag = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((props, ref) => jsx(GlobalTheme.Consumer, null, tokens => jsx(InnerRemovableTag, _extends({}, props, {
  mode: tokens.mode,
  ref: ref
})))));
export default RemovableTag;